/**
 * useOfflineSync — React hook for offline-first data access.
 *
 * Provides:
 *   - `isOnline` — current connectivity status
 *   - `pendingChanges` — number of unsynced writes
 *   - `isSyncing` — whether a flush is in progress
 *   - `syncNow()` — manually trigger a sync
 *   - `retryFailed()` — retry all permanently failed operations
 *
 * Automatically flushes the sync queue when the browser comes back online.
 *
 * @since 1.0.0
 */
import { useState, useEffect, useCallback } from 'react';
import { syncQueue } from '@/db/syncQueue';

export interface OfflineSyncState {
    /** Whether the browser is currently online */
    isOnline: boolean;
    /** Number of pending (unsynced) changes */
    pendingChanges: number;
    /** Whether a sync flush is currently running */
    isSyncing: boolean;
    /** Manually trigger a sync flush */
    syncNow: () => Promise<void>;
    /** Retry all failed sync entries */
    retryFailed: () => Promise<void>;
}

export function useOfflineSync(): OfflineSyncState {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [pendingChanges, setPendingChanges] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);

    // Track online/offline events
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            // Auto-flush when coming back online
            syncQueue.scheduleFlush(1500);
        };

        const handleOffline = () => {
            setIsOnline(false);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Subscribe to pending changes count
    useEffect(() => {
        const unsubscribe = syncQueue.subscribe((count) => {
            setPendingChanges(count);
        });

        return unsubscribe;
    }, []);

    // Manual sync trigger
    const syncNow = useCallback(async () => {
        if (!navigator.onLine) return;
        setIsSyncing(true);
        try {
            await syncQueue.flush();
        } finally {
            setIsSyncing(false);
        }
    }, []);

    // Retry failed entries
    const retryFailed = useCallback(async () => {
        await syncQueue.retryFailed();
    }, []);

    return {
        isOnline,
        pendingChanges,
        isSyncing,
        syncNow,
        retryFailed,
    };
}
